import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/auth-store';
import { isPersonalInfoComplete } from '@/lib/onboarding-completion';
import { hasActiveSubscription } from '@/lib/subscription';

export interface ProtectedRouteProps {
  children: ReactNode;
  requireProfileComplete?: boolean;
  requireEmailVerified?: boolean;
  allowUnverified?: boolean;
  requireActiveSubscription?: boolean;
}

/**
 * ProtectedRoute component that guards authenticated routes with granular access control
 *
 * Features:
 * - Checks auth token from Zustand store
 * - Redirects to login with return URL if not authenticated
 * - Handles session expiration with redirect
 * - Supports different levels of verification requirements
 * - Implements navigation guards based on user state
 *
 * Usage:
 * ```tsx
 * // Require authentication only
 * <ProtectedRoute>
 *   <Component />
 * </ProtectedRoute>
 *
 * // Require email verification
 * <ProtectedRoute requireEmailVerified={true}>
 *   <OnboardingStep />
 * </ProtectedRoute>
 *
 * // Require complete profile
 * <ProtectedRoute requireProfileComplete={true}>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * // Allow unverified users (for email verification pages)
 * <ProtectedRoute allowUnverified={true}>
 *   <VerifyEmail />
 * </ProtectedRoute>
 * ```
 */
export function ProtectedRoute({
  children,
  requireProfileComplete = false,
  requireEmailVerified = false,
  allowUnverified = false,
  requireActiveSubscription = false,
}: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, user, isProfileComplete, logout } = useAuthStore();

  // Check if user is authenticated
  const authenticated = isAuthenticated();

  useEffect(() => {
    // Handle session expiration - if we have a user but no token, logout
    const currentToken = useAuthStore.getState()?.token;
    if (user && !currentToken) {
      logout();
    }
  }, [user, logout]);

  // If not authenticated, redirect to login with return URL
  if (!authenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?returnUrl=${returnUrl}`} replace />;
  }

  // If email verification is required but user email is unverified
  if (requireEmailVerified && user?.emailStatus === 'UNVERIFIED') {
    return <Navigate to="/auth/verify-email" replace />;
  }

  // If unverified users are not allowed and email is unverified
  if (!allowUnverified && user?.emailStatus === 'UNVERIFIED') {
    return <Navigate to="/auth/verify-email" replace />;
  }

  // If profile completion is required but not complete, redirect to onboarding
  if (requireProfileComplete && !isProfileComplete) {
    const redirectTo = isPersonalInfoComplete(user) ? '/onboarding/terms' : '/onboarding/personal-info';
    return <Navigate to={redirectTo} replace />;
  }

  if (requireActiveSubscription && !hasActiveSubscription(user?.subscription)) {
    return <Navigate to="/dashboard/manage-plan" replace />;
  }

  // If authenticated and all requirements met, render children
  return <>{children}</>;
}
